import React, { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../lib/utils';
import { useLocale } from '../contexts/LocaleContext';
import { resourceDisplaysImageThumbnail } from '../utils/resource-display-utils';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './ui/hover-card';
import { Separator } from './ui/separator';
import { Badge } from './ui/badge';
import { getResourceKind, RESOURCE_KIND_ICONS, type ResourceEntityTag } from '../utils/resource-kind';
import { ResourceTitle } from './ResourceTitle';
import { EntityTagBadge } from './EntityTagsField';
import { stripHiddenLinkedEntitiesBlock } from '../utils/entity-mentions';
import { stripHtml } from '../utils/note-utils';
import { truncateLabel } from '../utils/text';
import { toBcp47Locale, type SupportedLocale } from '../locales/locale';
import { Checkbox } from './ui/checkbox';
import { S3ResourceImage } from './S3ResourceImage';
import {
  COLOR_RESOURCE_TAG_TAG_BG,
  COLOR_RESOURCE_TAG_TAG_TEXT,
  COLOR_RESOURCE_TAG_TAG_BORDER,
} from '../colors';

const TAG_BADGE_STYLE = {
  backgroundColor: COLOR_RESOURCE_TAG_TAG_BG,
  color: COLOR_RESOURCE_TAG_TAG_TEXT,
  borderColor: COLOR_RESOURCE_TAG_TAG_BORDER,
};

export interface ResourcesGridItem {
  id: string;
  title: string;
  mimeType: string | null;
  s3Key: string | null;
  url: string | null;
  naturalId: string | null;
  resourceDate: string | null;
  notes: string | null;
  fileName: string | null;
  createdAt: string;
  tags: string[];
}

const EMPTY_ENTITY_TAGS: ResourceEntityTag[] = [];

const noopToggleSelected = (): void => undefined;

function entityTagsEqual(a: ResourceEntityTag[], b: ResourceEntityTag[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let index = 0; index < a.length; index += 1) {
    if (a[index].id !== b[index].id || a[index].type !== b[index].type) {
      return false;
    }
  }
  return true;
}

function resourcesGridItemEqual(a: ResourcesGridItem, b: ResourcesGridItem): boolean {
  return (
    a.id === b.id &&
    a.title === b.title &&
    a.mimeType === b.mimeType &&
    a.s3Key === b.s3Key &&
    a.url === b.url &&
    a.naturalId === b.naturalId &&
    a.resourceDate === b.resourceDate &&
    a.notes === b.notes &&
    a.fileName === b.fileName &&
    a.createdAt === b.createdAt &&
    a.tags.length === b.tags.length &&
    a.tags.every((tag, index) => tag === b.tags[index])
  );
}

function formatResourceDate(iso: string, locale: SupportedLocale): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return iso;
  }
  return new Intl.DateTimeFormat(toBcp47Locale(locale), {
    dateStyle: 'short',
  }).format(d);
}

function useIsClipped(ref: React.RefObject<HTMLElement>, deps: React.DependencyList): boolean {
  const [isClipped, setIsClipped] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const check = (): void => {
      setIsClipped(el.scrollHeight > el.clientHeight + 1);
    };

    check();
    const resizeObserver = new ResizeObserver(check);
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return isClipped;
}

const ResourcesGridTile = React.memo(function ResourcesGridTile({
  resource,
  projectId,
  token,
  entityTags,
  onSelect,
  selectionEnabled,
  isSelected,
  onToggleSelected,
}: {
  resource: ResourcesGridItem;
  projectId: string;
  token: string;
  entityTags: ResourceEntityTag[];
  onSelect: (id: string, e: React.MouseEvent) => void;
  selectionEnabled: boolean;
  isSelected: boolean;
  onToggleSelected: (id: string, selected: boolean) => void;
}) {
  const { t, locale } = useLocale();
  const showImage = resourceDisplaysImageThumbnail(resource);
  const [directFailed, setDirectFailed] = useState(false);
  const useDirectUrl = Boolean(
    showImage && resource.url && !resource.s3Key && !directFailed,
  );
  const useS3Image = Boolean(showImage && resource.s3Key && !directFailed);
  const resourceKind = getResourceKind(resource);
  const TypeIcon = RESOURCE_KIND_ICONS[resourceKind];

  const imgClass = 'max-h-full max-w-full object-contain';

  const contentPreview = useMemo(
    () => (resource.notes ? stripHtml(stripHiddenLinkedEntitiesBlock(resource.notes)) : ''),
    [resource.notes]
  );

  const hoverRows = useMemo(() => {
    const rows: { label: string; value: string }[] = [];
    if (resource.resourceDate?.trim()) {
      rows.push({
        label: t('reference_date_label'),
        value: formatResourceDate(resource.resourceDate.trim(), locale),
      });
    }
    if (resource.createdAt?.trim()) {
      rows.push({
        label: t('creation_date'),
        value: formatResourceDate(resource.createdAt.trim(), locale),
      });
    }
    return rows;
  }, [resource, t, locale]);

  const hasImage = useS3Image || useDirectUrl;

  const tileInner = (
    <div
      className={cn(
        'relative isolate flex aspect-square min-h-0 w-full items-center justify-center overflow-hidden',
        hasImage ? 'p-0' : 'p-4'
      )}
    >
      <div className="relative z-0 flex h-full w-full items-center justify-center overflow-hidden">
        {useS3Image ? (
          <S3ResourceImage
            projectId={projectId}
            resourceId={resource.id}
            token={token}
            title={resource.title}
            className={imgClass}
          />
        ) : useDirectUrl ? (
          <img
            src={resource.url!}
            alt=""
            className={imgClass}
            onError={() => setDirectFailed(true)}
          />
        ) : (
          <div className="flex h-full min-h-0 w-full flex-col gap-1 overflow-hidden px-1 pt-2 text-left">
            <ResourceTitle resource={resource} className="shrink-0 text-sm font-medium" />
            {resourceKind === 'document' && (
              <span className="shrink-0 break-words text-sm font-medium text-foreground">{resource.title}</span>
            )}
            {contentPreview && (
              <span className="line-clamp-5 text-xs text-muted-foreground">{contentPreview}</span>
            )}
          </div>
        )}
      </div>
      {selectionEnabled && (
        <div
          className={cn(
            'absolute left-1.5 top-1.5 z-20 flex h-6 w-6 items-center justify-center rounded-full bg-card/90 shadow-sm transition-opacity',
            isSelected
              ? 'opacity-100'
              : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100'
          )}
          onClick={(e) => e.stopPropagation()}
          onAuxClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={isSelected}
            onCheckedChange={(value) => onToggleSelected(resource.id, !!value)}
            aria-label={t('resources_select_row')}
          />
        </div>
      )}
      {resourceKind === 'document-image' && (
        <span
          className={cn(
            'absolute top-1.5 z-10 mb-2 flex h-6 w-6 items-center justify-center rounded-full bg-card/90 shadow-sm',
            selectionEnabled
              ? cn(
                  'left-1.5 group-hover:left-8 [@media(hover:none)]:left-8',
                  isSelected && 'left-8'
                )
              : 'left-1.5'
          )}
        >
          <TypeIcon className="h-3.5 w-3.5 text-muted-foreground" />
        </span>
      )}
    </div>
  );

  const normalizedTags = (resource.tags || []).map((tag) => tag.trim().toLowerCase()).filter(Boolean);

  const tagBadges = (normalizedTags.length > 0 || entityTags.length > 0) && (
    <div className="flex flex-wrap gap-1">
      {entityTags.map((entityTag) => (
        <EntityTagBadge
          key={`${entityTag.type}-${entityTag.id}`}
          entityType={entityTag.type}
          id={entityTag.id}
          name={entityTag.name}
          documentId={entityTag.documentId}
          className="h-5 max-w-[110px] gap-1 px-2 py-0.5 text-[10px] leading-none"
          maxLabelLength={15}
        />
      ))}
      {normalizedTags.map((tag) => (
        <Badge key={tag} variant="secondary" className="h-5 max-w-[110px] truncate rounded-full px-2 py-0.5 text-[10px] leading-none" style={TAG_BADGE_STYLE} title={tag}>
          {truncateLabel(tag, 15)}
        </Badge>
      ))}
    </div>
  );

  const tagsContainerRef = useRef<HTMLDivElement>(null);
  const hasMoreTags = useIsClipped(tagsContainerRef, [normalizedTags, entityTags]);

  const tileFooter = tagBadges && (
    <div className="flex flex-col gap-1.5 border-t border-border p-3">
      <div className="relative">
        <div ref={tagsContainerRef} className="max-h-11 overflow-hidden">
          {tagBadges}
        </div>
        {hasMoreTags && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute bottom-0 right-0 flex h-5 min-w-[20px] items-center justify-center rounded-full border border-border bg-card px-1.5 text-[10px] font-semibold leading-none text-muted-foreground shadow-sm"
          >
            +
          </span>
        )}
      </div>
    </div>
  );

  const button = (
    <div
      onClick={(e) => onSelect(resource.id, e)}
      onAuxClick={(e) => onSelect(resource.id, e)}
      className={cn(
        'group flex w-full cursor-pointer flex-col overflow-hidden rounded-lg border border-border bg-card text-left transition-[transform,box-shadow,border-color] duration-200 ease-out hover:-translate-y-0.5 hover:border-[var(--scribane-green)] hover:shadow-[0_12px_24px_-8px_rgba(94,204,128,0.5)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isSelected && 'border-[var(--scribane-green)] ring-1 ring-[var(--scribane-green)]'
      )}
    >
      {tileInner}
      {tileFooter}
    </div>
  );

  if (hoverRows.length === 0 && !tagBadges) {
    return button;
  }

  return (
    <HoverCard openDelay={250} closeDelay={100}>
      <HoverCardTrigger asChild>{button}</HoverCardTrigger>
      <HoverCardContent side="bottom" align="center" className="w-72 max-w-[min(18rem,calc(100vw-2rem))]">
        <div className="space-y-2.5 text-sm">
          {hoverRows.map((row) => (
            <div key={row.label}>
              <div className="text-xs font-medium text-muted-foreground">{row.label}</div>
              <div className="break-words text-foreground">{row.value}</div>
            </div>
          ))}
          {tagBadges && (
            <>
              <Separator />
              {tagBadges}
            </>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}, (prev, next) =>
  prev.projectId === next.projectId &&
  prev.token === next.token &&
  prev.selectionEnabled === next.selectionEnabled &&
  prev.isSelected === next.isSelected &&
  prev.onSelect === next.onSelect &&
  prev.onToggleSelected === next.onToggleSelected &&
  resourcesGridItemEqual(prev.resource, next.resource) &&
  entityTagsEqual(prev.entityTags, next.entityTags)
);

export const ResourcesGrid: React.FC<{
  resources: ResourcesGridItem[];
  projectId: string;
  token: string;
  resourceEntityTags?: Map<string, ResourceEntityTag[]>;
  onSelectResource: (id: string, e: React.MouseEvent) => void;
  emptyLabel: string;
  selectionEnabled?: boolean;
  selectedResourceIds?: ReadonlySet<string>;
  onToggleResourceSelected?: (id: string, selected: boolean) => void;
}> = ({
  resources,
  projectId,
  token,
  resourceEntityTags,
  onSelectResource,
  emptyLabel,
  selectionEnabled = false,
  selectedResourceIds,
  onToggleResourceSelected,
}) => {
  if (resources.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-8 text-center text-muted-foreground">
        {emptyLabel}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3 p-3">
      {resources.map((resource) => (
        <ResourcesGridTile
          key={resource.id}
          resource={resource}
          projectId={projectId}
          token={token}
          entityTags={resourceEntityTags?.get(resource.id) ?? EMPTY_ENTITY_TAGS}
          onSelect={onSelectResource}
          selectionEnabled={selectionEnabled}
          isSelected={selectedResourceIds?.has(resource.id) ?? false}
          onToggleSelected={onToggleResourceSelected ?? noopToggleSelected}
        />
      ))}
    </div>
  );
};
